import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import {
    ChevronUp, ChevronDown,
    BookOpen, List, Bookmark, BookmarkCheck, Highlighter,
    X, ArrowLeft, Trash2, ExternalLink, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EbookReader() {
    const [params] = useSearchParams();
    const token = params.get('token');
    const navigate = useNavigate();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [html, setHtml] = useState('');
    const [styles, setStyles] = useState('');
    const [toc, setToc] = useState([]);

    const [scrollPosition, setScrollPosition] = useState(0);
    const [currentChapter, setCurrentChapter] = useState(null);
    const [bookmarks, setBookmarks] = useState([]);
    const [highlights, setHighlights] = useState([]);

    // Refs mirror state so closures always read current values
    const bookmarksRef = useRef([]);
    const highlightsRef = useRef([]);
    const currentChapterRef = useRef(null);
    const scrollPositionRef = useRef(0);

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [sidebarTab, setSidebarTab] = useState('toc');
    const [showSelectionPopup, setShowSelectionPopup] = useState(null);
    const [saveStatus, setSaveStatus] = useState('');
    const [expandedHighlight, setExpandedHighlight] = useState(null);

    const contentRef = useRef(null);
    const saveTimerRef = useRef(null);
    const lastSaveRef = useRef(null);
    const savedScrollRef = useRef(0);
    const savedChapterRef = useRef(null);
    const pendingHighlightRef = useRef(null);

    // Sync refs
    useEffect(() => { bookmarksRef.current = bookmarks; }, [bookmarks]);
    useEffect(() => { highlightsRef.current = highlights; }, [highlights]);
    useEffect(() => { currentChapterRef.current = currentChapter; }, [currentChapter]);
    useEffect(() => { scrollPositionRef.current = scrollPosition; }, [scrollPosition]);

    // ─── LOAD ────────────────────────────────────────────────────
    useEffect(() => {
        if (!token) { setError('No access token provided'); setLoading(false); return; }
        (async () => {
            try {
                // Cache-bust the content fetch so every reader load gets the
                // latest manuscript from storage — defeats stale browser /
                // intermediate caches that still hold the previous version.
                const cacheBust = Date.now();
                const [cRes, sRes] = await Promise.all([
                    axios.get(`${API}/ebook/content?token=${token}&_=${cacheBust}`, {
                        withCredentials: true,
                        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
                    }),
                    axios.get(`${API}/ebook/reader-state?token=${token}`, { withCredentials: true }),
                ]);
                setHtml(cRes.data.html);
                setStyles(cRes.data.styles);
                setToc(cRes.data.toc);
                const st = sRes.data;
                savedScrollRef.current = st.scroll_position || 0;
                savedChapterRef.current = st.current_chapter || null;
                setScrollPosition(st.scroll_position || 0);
                setCurrentChapter(st.current_chapter || null);
                setBookmarks(st.bookmarks || []);
                setHighlights(st.highlights || []);
            } catch (err) {
                setError(err.response?.data?.detail || 'Failed to load e-book');
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    // ─── WIRE UP BUILT-IN TOC + RESTORE POSITION ────────────────
    useEffect(() => {
        if (loading || !html || !contentRef.current || !toc.length) return;
        const container = contentRef.current;

        // Computes the pixel offset caused by the ebook's own sticky top bar.
        // The new manuscript HTML uses a single sticky element: .ebook-topbar.
        // Looking it up directly avoids false-positives from heuristic
        // selectors that overcount when multiple matches happen to satisfy
        // them on later pages.
        const computeStickyOffset = () => {
            if (!container) return 0;
            const tb = container.querySelector('.ebook-topbar');
            if (!tb) return 0;
            const style = window.getComputedStyle(tb);
            if (style.position !== 'sticky' && style.position !== 'fixed') return 0;
            return Math.round(tb.getBoundingClientRect().height);
        };

        // Scroll the given element so its TOP edge lands immediately below any
        // sticky element at the top of the container.
        const scrollElementToTop = (id) => {
            if (!id) return;
            const el = container.querySelector('[id="' + String(id).replace(/"/g, '') + '"]');
            if (!el) return;
            // If the anchor is an inline `<a>` directly before a heading, prefer
            // the heading element for alignment.
            let target = el;
            const next = el.nextElementSibling;
            if (el.tagName === 'A' && next && /^H[1-6]$/.test(next.tagName)) {
                target = next;
            }
            const offset = computeStickyOffset();
            const elRect = target.getBoundingClientRect();
            const contRect = container.getBoundingClientRect();
            const delta = elRect.top - contRect.top - offset;
            // Use instant scroll so the target lands precisely on the first
            // frame — smooth scrolling over long distances can stop mid-way.
            const prev = container.style.scrollBehavior;
            container.style.scrollBehavior = 'auto';
            container.scrollTop = container.scrollTop + delta;
            // Re-measure on the next frame in case sticky heights changed due
            // to responsive layout and correct any residual offset.
            requestAnimationFrame(() => {
                const off2 = computeStickyOffset();
                const r2 = target.getBoundingClientRect();
                const c2 = container.getBoundingClientRect();
                const d2 = r2.top - c2.top - off2;
                if (Math.abs(d2) > 2) container.scrollTop = container.scrollTop + d2;
                container.style.scrollBehavior = prev;
            });
        };

        window.scrollToSection = scrollElementToTop;
        window._readerScrollToId = scrollElementToTop;
        window.toggleSidebar = () => {
            window._toggleOurSidebar?.();
        };

        // Restore scroll position — percentage-based (works across reflow).
        // Order: 1) sessionStorage (per-session immediate restore on reload)
        //        2) database scroll_position from reader-state API
        //        3) start at top
        // We must wait until the dangerouslySetInnerHTML content has been laid
        // out before computing the target scrollTop. We poll until scrollHeight
        // stabilises so reflows from font/image loading can't shift the result.
        const dbPct = savedScrollRef.current;
        let targetPct = 0;
        try {
            const ssRaw = token ? sessionStorage.getItem(`reader-scroll-${token}`) : null;
            const ssPct = ssRaw ? parseFloat(ssRaw) : NaN;
            if (!isNaN(ssPct) && ssPct > 0.5) targetPct = ssPct;
            else if (dbPct > 0.5) targetPct = dbPct;
        } catch (_) {
            if (dbPct > 0.5) targetPct = dbPct;
        }

        if (targetPct > 0) {
            // Suppress the scroll-listener save during programmatic restore so
            // the saved position isn't accidentally overwritten with 0 on the
            // initial scroll event.
            const restoreUntil = Date.now() + 1500;
            const guard = () => Date.now() < restoreUntil;
            window._readerRestoreGuard = guard;
            const apply = () => {
                if (!container) return;
                const max = container.scrollHeight - container.clientHeight;
                container.scrollTop = (targetPct / 100) * max;
            };
            // First apply, then re-apply on the next two animation frames and
            // again at 200/600ms to absorb late layout (font load, image dims).
            requestAnimationFrame(() => {
                apply();
                requestAnimationFrame(apply);
                setTimeout(apply, 200);
                setTimeout(apply, 600);
            });
        }

        // Restore highlights
        setTimeout(() => restoreHighlightsToDOM(), 800);
    }, [loading, html, toc]);


    // Expose sidebar toggle globally so ebook's ☰ button uses our sidebar
    useEffect(() => {
        window._toggleOurSidebar = () => setSidebarOpen(prev => !prev);
        return () => { delete window._toggleOurSidebar; };
    }, []);

    // ─── SCROLL TRACKING ─────────────────────────────────────────
    const handleScroll = useCallback(() => {
        const el = contentRef.current;
        if (!el) return;
        // While a programmatic restore is in flight, do NOT save — otherwise
        // the listener fires before the restore completes and overwrites the
        // saved position with the current (still-at-top) scrollTop.
        const guard = window._readerRestoreGuard;
        if (typeof guard === 'function' && guard()) return;
        const pct = el.scrollHeight > el.clientHeight ? (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100 : 0;
        setScrollPosition(pct);
        // Detect current chapter
        const headings = el.querySelectorAll('h2.section-heading[id]');
        let active = null;
        const cRect = el.getBoundingClientRect();
        for (const h of headings) {
            if (h.getBoundingClientRect().top <= cRect.top + 200) active = h.id;
        }
        if (active && active !== currentChapterRef.current) setCurrentChapter(active);
        // Save to sessionStorage immediately (per-session restore on reload).
        if (token) {
            try { sessionStorage.setItem(`reader-scroll-${token}`, String(pct)); } catch (_) {}
        }
        // Debounce DB save to 500ms after the user stops scrolling.
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => autoSave(pct, active || currentChapterRef.current), 500);
    }, [token]);

    useEffect(() => {
        const el = contentRef.current;
        if (el) { el.addEventListener('scroll', handleScroll, { passive: true }); return () => el.removeEventListener('scroll', handleScroll); }
    }, [handleScroll, html]);

    useEffect(() => {
        const onUnload = () => {
            if (!token || !contentRef.current) return;
            const el = contentRef.current;
            const pct = el.scrollHeight > el.clientHeight ? (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100 : 0;
            navigator.sendBeacon(`${API}/ebook/reader-state-beacon`, JSON.stringify({ token, scroll_position: pct, current_chapter: currentChapterRef.current }));
        };
        window.addEventListener('beforeunload', onUnload);
        return () => window.removeEventListener('beforeunload', onUnload);
    }, [token]);

    // ─── SAVE ────────────────────────────────────────────────────
    const autoSave = async (pct, chapter) => {
        const now = Date.now();
        if (lastSaveRef.current && now - lastSaveRef.current < 1500) return;
        lastSaveRef.current = now;
        try { await axios.put(`${API}/ebook/reader-state`, { token, scroll_position: pct, current_chapter: chapter, bookmarks: bookmarksRef.current, highlights: highlightsRef.current }, { withCredentials: true }); } catch {}
    };

    const saveState = async (bm, hl) => {
        const b = bm || bookmarksRef.current;
        const h = hl || highlightsRef.current;
        try {
            setSaveStatus('saving');
            await axios.put(`${API}/ebook/reader-state`, { token, scroll_position: scrollPositionRef.current, current_chapter: currentChapterRef.current, bookmarks: b, highlights: h }, { withCredentials: true });
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
        } catch { setSaveStatus('error'); }
    };

    // ─── NAVIGATION ──────────────────────────────────────────────
    const scrollToChapter = (chapterId) => {
        if (!contentRef.current) return;
        setCurrentChapter(chapterId);
        // Close the sidebar FIRST so the content reflows, then scroll after
        // React has committed the layout change. Otherwise the sidebar-close
        // reflow invalidates the scroll position and the target ends up ~110px
        // off, showing unrelated content at the top.
        setSidebarOpen(false);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (typeof window._readerScrollToId === 'function') {
                    window._readerScrollToId(chapterId);
                }
            });
        });
    };
    const scrollPage = (dir) => {
        if (!contentRef.current) return;
        contentRef.current.scrollBy({ top: dir * contentRef.current.clientHeight * 0.5, behavior: 'smooth' });
    };

    // ─── BOOKMARKS ───────────────────────────────────────────────
    const toggleBookmark = () => {
        if (!currentChapterRef.current) return;
        const idx = bookmarksRef.current.findIndex(b => b.chapter_id === currentChapterRef.current);
        let newBm;
        if (idx >= 0) { newBm = bookmarksRef.current.filter((_, i) => i !== idx); }
        else {
            const ch = toc.find(t => t.id === currentChapterRef.current);
            newBm = [...bookmarksRef.current, { bookmark_id: `bm-${Date.now()}`, chapter_id: currentChapterRef.current, chapter_title: ch?.title || currentChapterRef.current, scroll_position: scrollPositionRef.current, created_at: new Date().toISOString() }];
        }
        setBookmarks(newBm);
        saveState(newBm, null);
    };
    const removeBookmark = (id) => { const n = bookmarksRef.current.filter(b => b.bookmark_id !== id); setBookmarks(n); saveState(n, null); };
    const isBookmarked = bookmarks.some(b => b.chapter_id === currentChapter);

    // ─── HIGHLIGHTS ──────────────────────────────────────────────
    const handleTextSelection = useCallback((e) => {
        if (e?.target?.closest?.('[data-testid="highlight-popup"]')) return;
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed || !sel.toString().trim()) {
            if (!e?.target?.closest?.('[data-testid="highlight-popup"]')) { setShowSelectionPopup(null); pendingHighlightRef.current = null; }
            return;
        }
        const text = sel.toString().trim();
        if (text.length < 2) return;
        // NO upper limit on text length
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const cRect = contentRef.current?.getBoundingClientRect();
        if (!cRect) return;
        pendingHighlightRef.current = { text, range: range.cloneRange() };
        setShowSelectionPopup({ x: rect.left - cRect.left + rect.width / 2, y: rect.top - cRect.top, text });
    }, []);

    useEffect(() => {
        document.addEventListener('mouseup', handleTextSelection);
        return () => document.removeEventListener('mouseup', handleTextSelection);
    }, [handleTextSelection]);

    const addHighlight = (color = 'gold') => {
        const pending = pendingHighlightRef.current;
        if (!pending?.text) return;
        // Determine the printed page number where the selection was made by
        // scanning forward from the selection for the nearest book-page-number
        // marker (every page-block in the new HTML ends with one).
        let pageLabel = '';
        try {
            const r = pending.range;
            let node = r.startContainer;
            const root = contentRef.current;
            // Walk up to a containing element we can use as a starting point
            const startEl = node.nodeType === 1 ? node : node.parentElement;
            // Look forward (next siblings + descendants) for a .book-page-number
            const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
                acceptNode: (el) => el.classList?.contains('book-page-number') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
            });
            let found = null;
            let cur = walker.nextNode();
            while (cur) {
                // Only accept markers that come at or after the selection
                if (startEl && (startEl.compareDocumentPosition(cur) & Node.DOCUMENT_POSITION_FOLLOWING || startEl === cur || startEl.contains(cur))) {
                    found = cur; break;
                }
                cur = walker.nextNode();
            }
            if (found) {
                const m = (found.textContent || '').match(/\d+/);
                if (m) pageLabel = m[0];
            }
        } catch (_) {}
        // Store a unique anchor marker in the DOM so we can find it later
        const hlId = `hl-${Date.now()}`;
        const newHl = {
            highlight_id: hlId,
            text: pending.text,
            chapter_id: currentChapterRef.current,
            chapter_title: toc.find(t => t.id === currentChapterRef.current)?.title || '',
            page_label: pageLabel,
            color,
            // Store first 60 chars for DOM matching on reload
            match_key: pending.text.substring(0, 60),
            start_offset: 0, end_offset: 0, container_path: '',
            created_at: new Date().toISOString(),
        };
        const newHls = [...highlightsRef.current, newHl];
        setHighlights(newHls);
        // Wrap selected range in DOM
        try {
            const range = pending.range;
            const span = document.createElement('span');
            span.className = `reader-highlight reader-highlight-${color}`;
            span.dataset.highlightId = hlId;
            range.surroundContents(span);
        } catch {
            // surroundContents fails for cross-node selections — use highlight API or mark approach
            try {
                const range = pending.range;
                const contents = range.extractContents();
                const span = document.createElement('span');
                span.className = `reader-highlight reader-highlight-${color}`;
                span.dataset.highlightId = hlId;
                span.appendChild(contents);
                range.insertNode(span);
            } catch {}
        }
        window.getSelection()?.removeAllRanges();
        setShowSelectionPopup(null);
        pendingHighlightRef.current = null;
        saveState(null, newHls);
    };

    const removeHighlight = (hlId) => {
        const newHl = highlightsRef.current.filter(h => h.highlight_id !== hlId);
        setHighlights(newHl);
        const el = contentRef.current?.querySelector(`[data-highlight-id="${hlId}"]`);
        if (el) { const p = el.parentNode; while (el.firstChild) p.insertBefore(el.firstChild, el); p.removeChild(el); }
        saveState(null, newHl);
    };

    const jumpToHighlight = (hl) => {
        const container = contentRef.current;
        if (!container) return;

        // Locate the target element in the DOM. Prefer the wrapped highlight
        // span (added at creation); fall back to a flexible cross-node text
        // search since the new HTML splits each line into its own text-line
        // span — so a long search key never sits in a single text node.
        const findTarget = () => {
            const el = container.querySelector(`[data-highlight-id="${hl.highlight_id}"]`);
            if (el) return el;
            const fullText = (hl.text || hl.match_key || '').replace(/\s+/g, ' ').trim();
            if (!fullText) return null;
            // Try several decreasing-length keys. Start with first 6-10 words,
            // shorten as needed. Each attempt walks text nodes, concatenates
            // their contents normalised by whitespace, and finds the first
            // location where the key matches.
            const tryKeys = [];
            const words = fullText.split(' ');
            for (const wc of [10, 8, 6, 4, 3]) {
                if (words.length >= wc) tryKeys.push(words.slice(0, wc).join(' '));
            }
            tryKeys.push(fullText.substring(0, 40));
            tryKeys.push(fullText.substring(0, 20));

            // Build a normalised concat of all text nodes once with an offset map.
            const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
            const parts = [];
            let combined = '';
            let n;
            while ((n = walker.nextNode())) {
                const t = n.textContent || '';
                if (!t.trim()) continue;
                // Normalise whitespace; map back to the source node
                const norm = t.replace(/\s+/g, ' ');
                parts.push({ node: n, start: combined.length, end: combined.length + norm.length });
                combined += norm + ' ';  // add a separator so adjacent nodes don't merge tokens
            }
            for (const key of tryKeys) {
                if (!key || key.length < 3) continue;
                const idx = combined.indexOf(key);
                if (idx === -1) continue;
                // Find the part covering this index
                for (const p of parts) {
                    if (p.start <= idx && idx < p.end + 2) {
                        return p.node.parentElement || null;
                    }
                }
            }
            return null;
        };

        // Close the sidebar FIRST so the desktop flex-sibling reflow happens
        // BEFORE we compute scroll position. Otherwise the layout shift after
        // scrolling pushes the target ~110px off and the user lands on
        // unrelated content (or, if the document didn't reflow yet, lands
        // somewhere stale because smooth-scroll was interrupted).
        setSidebarOpen(false);

        // Two rAF ticks wait for React to commit the close, the layout to
        // reflow, and the scroll-behavior CSS to take effect. Then scroll
        // instantly with sticky-topbar offset, the same way TOC clicks work.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const target = findTarget();
                if (!target) return;
                const tb = container.querySelector('.ebook-topbar');
                let tbH = 0;
                if (tb) {
                    const s = window.getComputedStyle(tb);
                    if (s.position === 'sticky' || s.position === 'fixed') {
                        tbH = Math.round(tb.getBoundingClientRect().height);
                    }
                }
                const r = target.getBoundingClientRect();
                const c = container.getBoundingClientRect();
                // Center the highlight in the viewport (below the topbar) so
                // it's clearly visible. Falls back to top-align if the target
                // is taller than the viewport.
                const viewportH = container.clientHeight - tbH;
                const targetH = r.height;
                let delta;
                if (targetH < viewportH * 0.7) {
                    delta = r.top - c.top - tbH - (viewportH - targetH) / 2;
                } else {
                    delta = r.top - c.top - tbH;
                }
                const prev = container.style.scrollBehavior;
                container.style.scrollBehavior = 'auto';
                container.scrollTop = container.scrollTop + delta;
                // Re-apply on the next frame to absorb late layout shifts.
                requestAnimationFrame(() => {
                    const r2 = target.getBoundingClientRect();
                    const c2 = container.getBoundingClientRect();
                    let d2;
                    if (targetH < viewportH * 0.7) {
                        d2 = r2.top - c2.top - tbH - (viewportH - targetH) / 2;
                    } else {
                        d2 = r2.top - c2.top - tbH;
                    }
                    if (Math.abs(d2) > 2) container.scrollTop = container.scrollTop + d2;
                    container.style.scrollBehavior = prev;
                    // Briefly flash the highlight so the user can see WHERE
                    // they landed even if it's text-only (no wrapped span).
                    target.classList.add('reader-highlight-flash');
                    setTimeout(() => target.classList.remove('reader-highlight-flash'), 2200);
                });
            });
        });
    };

    const restoreHighlightsToDOM = () => {
        if (!contentRef.current) return;
        highlightsRef.current.forEach(hl => {
            if (!hl.text || hl.text.length < 2) return;
            if (contentRef.current.querySelector(`[data-highlight-id="${hl.highlight_id}"]`)) return;
            const searchKey = (hl.match_key || hl.text.substring(0, 60)).substring(0, 40);
            if (!searchKey) return;
            const walker = document.createTreeWalker(contentRef.current, NodeFilter.SHOW_TEXT, null);
            let node;
            while ((node = walker.nextNode())) {
                const idx = node.textContent.indexOf(searchKey);
                if (idx >= 0) {
                    try {
                        const endIdx = Math.min(idx + Math.max(searchKey.length, hl.text.length), node.textContent.length);
                        const range = document.createRange();
                        range.setStart(node, idx);
                        range.setEnd(node, endIdx);
                        const span = document.createElement('span');
                        span.className = `reader-highlight reader-highlight-${hl.color}`;
                        span.dataset.highlightId = hl.highlight_id;
                        range.surroundContents(span);
                    } catch {}
                    break;
                }
            }
        });
    };

    const HIGHLIGHT_COLORS = [
        { name: 'gold', css: '#fde68a' },
        { name: 'green', css: '#bbf7d0' },
        { name: 'blue', css: '#bfdbfe' },
        { name: 'pink', css: '#fbcfe8' },
    ];

    const progressPct = Math.min(100, Math.max(0, scrollPosition));
    const currentChapterTitle = toc.find(t => t.id === currentChapter)?.title || 'Start';

    // ─── LOADING / ERROR ─────────────────────────────────────────
    if (loading) return (
        <div className="min-h-screen bg-[#0B1235] flex items-center justify-center" data-testid="reader-loading">
            <BookOpen className="w-12 h-12 text-[#D4AF37] mx-auto mb-4 animate-pulse" />
            <p className="text-white/70 font-body text-lg ml-3">Loading your e-book...</p>
        </div>
    );
    if (error) return (
        <div className="min-h-screen bg-[#0B1235] flex items-center justify-center" data-testid="reader-error">
            <div className="text-center max-w-md px-6">
                <p className="text-red-400 font-body text-lg mb-4">{error}</p>
                <button onClick={() => navigate('/')} className="bg-[#D4AF37] text-[#0B1235] font-bold px-6 py-3 rounded font-body" data-testid="reader-go-home-btn">Go Home</button>
            </div>
        </div>
    );

    return (
        <div className="h-screen flex flex-col bg-[#0B1235] overflow-hidden" data-testid="ebook-reader">
            <Helmet>
                <title>Reading: Know Your Stuff — Alice M. White</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            {/* ─── TOP BAR ─────────────────────────────────── */}
            <header className="bg-[#0B1235] border-b border-[#D4AF37]/30 px-4 py-3 flex items-center gap-3 shrink-0 z-30" data-testid="reader-header">
                <button onClick={() => navigate('/')} className="text-white/60 hover:text-[#D4AF37] transition-colors shrink-0" data-testid="reader-back-btn"><ArrowLeft size={20} /></button>
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/60 hover:text-[#D4AF37] transition-colors shrink-0" data-testid="reader-menu-btn"><List size={20} /></button>
                <div className="flex-1 min-w-0 mx-2">
                    <p className="text-white/50 text-xs font-body truncate" data-testid="reader-chapter-label">{currentChapterTitle}</p>
                    <div className="w-full bg-white/10 rounded-full h-1.5 mt-1">
                        <div className="bg-[#D4AF37] h-1.5 rounded-full transition-all duration-300" style={{ width: `${progressPct}%` }} data-testid="reader-progress-bar" />
                    </div>
                </div>
                <span className="text-white/40 text-xs font-body shrink-0" data-testid="reader-progress-text">{Math.round(progressPct)}%</span>
                <button onClick={toggleBookmark} className={`ml-1 transition-colors shrink-0 ${isBookmarked ? 'text-[#D4AF37]' : 'text-white/40 hover:text-[#D4AF37]'}`} data-testid="reader-bookmark-btn">
                    {isBookmarked ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
                </button>
                {/* Nav arrows in header for mobile */}
                <div className="flex items-center gap-1 ml-1 md:hidden">
                    <button onClick={() => scrollPage(-1)} className="text-[#D4AF37] p-1" data-testid="reader-prev-btn-mobile"><ChevronUp size={18} /></button>
                    <button onClick={() => scrollPage(1)} className="text-[#D4AF37] p-1" data-testid="reader-next-btn-mobile"><ChevronDown size={18} /></button>
                </div>
                {saveStatus === 'saving' && <span className="text-white/30 text-xs ml-1 shrink-0 hidden md:inline">Saving...</span>}
                {saveStatus === 'saved' && <span className="text-[#D4AF37]/60 text-xs ml-1 shrink-0 hidden md:inline">Saved</span>}
            </header>

            <div className="flex flex-1 overflow-hidden relative">
                {/* ─── SIDEBAR ─────────────────────────────── */}
                {sidebarOpen && (
                    <aside className="w-72 max-w-[80vw] bg-[#0f1a45] border-r border-[#D4AF37]/20 flex flex-col shrink-0 z-20 absolute md:relative h-full" data-testid="reader-sidebar">
                        <div className="flex items-center justify-between px-3 py-3 border-b border-[#D4AF37]/20">
                            <div className="flex gap-1">
                                {[{ key: 'toc', label: 'Contents', icon: List }, { key: 'bookmarks', label: 'Saved', icon: Bookmark }, { key: 'highlights', label: 'Highlights', icon: Highlighter }].map(({ key, label, icon: Icon }) => (
                                    <button key={key} onClick={() => setSidebarTab(key)} className={`px-2 py-1.5 text-xs font-body rounded transition-colors flex items-center gap-1 ${sidebarTab === key ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-white/50 hover:text-white/80'}`} data-testid={`sidebar-tab-${key}`}>
                                        <Icon size={13} /> {label}
                                    </button>
                                ))}
                            </div>
                            <button onClick={() => setSidebarOpen(false)} className="text-white/40 hover:text-white" data-testid="sidebar-close-btn"><X size={18} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto py-2">
                            {/* TOC */}
                            {sidebarTab === 'toc' && (
                                <nav data-testid="sidebar-toc-list">
                                    {toc.map(ch => (
                                        <button key={ch.id} onClick={() => scrollToChapter(ch.id)} className={`w-full text-left px-4 py-2.5 text-sm font-body transition-colors border-l-2 flex items-center justify-between ${currentChapter === ch.id ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'border-transparent text-white/60 hover:text-white hover:bg-white/5'}`} data-testid={`toc-item-${ch.id}`}>
                                            <span>{ch.title}</span>
                                            {ch.page && <span className="text-white/30 text-xs ml-2 shrink-0">{ch.page}</span>}
                                        </button>
                                    ))}
                                </nav>
                            )}
                            {/* Bookmarks */}
                            {sidebarTab === 'bookmarks' && (
                                <div data-testid="sidebar-bookmarks-list" className="px-2">
                                    {bookmarks.length === 0 ? <p className="text-white/30 text-sm font-body text-center py-8">No bookmarks yet.</p> : bookmarks.map(bm => (
                                        <div key={bm.bookmark_id} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-white/5 group">
                                            <BookmarkCheck size={14} className="text-[#D4AF37] shrink-0" />
                                            <button onClick={() => scrollToChapter(bm.chapter_id)} className="flex-1 text-left text-sm text-white/70 hover:text-[#D4AF37] font-body truncate">{bm.chapter_title}</button>
                                            <button onClick={() => removeBookmark(bm.bookmark_id)} className="text-white/20 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"><Trash2 size={13} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Highlights */}
                            {sidebarTab === 'highlights' && (
                                <div data-testid="sidebar-highlights-list" className="px-2 space-y-1">
                                    {highlights.length === 0 ? <p className="text-white/30 text-sm font-body text-center py-8">No highlights yet. Select text to highlight.</p> : highlights.map(hl => (
                                        <div key={hl.highlight_id} className="rounded hover:bg-white/5 group" data-testid={`highlight-item-${hl.highlight_id}`}>
                                            <div className="flex items-start gap-2 px-2 py-2">
                                                <div className={`w-3 h-3 rounded-full shrink-0 mt-0.5 ${hl.color === 'gold' ? 'bg-yellow-300' : hl.color === 'green' ? 'bg-green-300' : hl.color === 'blue' ? 'bg-blue-300' : 'bg-pink-300'}`} />
                                                <div className="flex-1 min-w-0">
                                                    {/* Full text — clicking the text toggles expand/collapse so the user can read the entire highlight in the menu. */}
                                                    <button
                                                        onClick={() => setExpandedHighlight(expandedHighlight === hl.highlight_id ? null : hl.highlight_id)}
                                                        className="text-left w-full hover:text-white/90 transition-colors"
                                                        data-testid={`expand-highlight-${hl.highlight_id}`}
                                                        title={expandedHighlight === hl.highlight_id ? 'Collapse' : 'View full highlight'}
                                                    >
                                                        <p className={`text-white/60 text-xs font-body ${expandedHighlight === hl.highlight_id ? '' : 'line-clamp-3'}`}>
                                                            "{hl.text}"
                                                        </p>
                                                    </button>
                                                    {hl.text.length > 120 && (
                                                        <button onClick={() => setExpandedHighlight(expandedHighlight === hl.highlight_id ? null : hl.highlight_id)} className="text-[#D4AF37]/60 text-[10px] font-body mt-0.5 hover:text-[#D4AF37] flex items-center gap-0.5">
                                                            {expandedHighlight === hl.highlight_id ? 'Show less' : 'Show full text'}
                                                            <ChevronRight size={10} className={`transition-transform ${expandedHighlight === hl.highlight_id ? 'rotate-90' : ''}`} />
                                                        </button>
                                                    )}
                                                    {/* Source line: page number + chapter title */}
                                                    <p className="text-white/30 text-[10px] font-body mt-1" data-testid={`highlight-source-${hl.highlight_id}`}>
                                                        {hl.page_label && <span>Page {hl.page_label}</span>}
                                                        {hl.page_label && hl.chapter_title && <span className="mx-1">·</span>}
                                                        {hl.chapter_title}
                                                    </p>
                                                    {/* Separate Go-to-highlight button — always visible, in addition to the expand toggle */}
                                                    <button
                                                        onClick={() => jumpToHighlight(hl)}
                                                        className="mt-1.5 inline-flex items-center gap-1 text-[#D4AF37] hover:text-yellow-200 text-[11px] font-body font-semibold"
                                                        data-testid={`jump-highlight-${hl.highlight_id}`}
                                                    >
                                                        Go to highlight
                                                        <ChevronRight size={11} />
                                                    </button>
                                                </div>
                                                <button onClick={() => removeHighlight(hl.highlight_id)} className="text-white/20 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5"><Trash2 size={13} /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </aside>
                )}

                {/* ─── CONTENT ─────────────────────────────── */}
                <div className="flex-1 flex flex-col overflow-hidden relative">
                    {showSelectionPopup && (
                        <div className="absolute z-50 flex items-center gap-1 bg-[#0B1235] border border-[#D4AF37]/40 rounded-lg shadow-xl px-2 py-1.5"
                            style={{ left: Math.max(10, Math.min(showSelectionPopup.x - 60, (contentRef.current?.clientWidth || 400) - 140)), top: showSelectionPopup.y < 60 ? showSelectionPopup.y + 30 : showSelectionPopup.y - 50 }}
                            data-testid="highlight-popup" onMouseDown={e => e.preventDefault()}>
                            {HIGHLIGHT_COLORS.map(c => (
                                <button key={c.name} onClick={() => addHighlight(c.name)} className="w-7 h-7 rounded-full border-2 border-white/20 hover:border-white/60 transition-colors" style={{ backgroundColor: c.css }} data-testid={`highlight-color-${c.name}`} />
                            ))}
                            <button onClick={() => { window.getSelection()?.removeAllRanges(); setShowSelectionPopup(null); }} className="text-white/40 hover:text-white ml-1" data-testid="highlight-cancel-btn"><X size={14} /></button>
                        </div>
                    )}

                    <div ref={contentRef} className="flex-1 overflow-y-auto reader-content-area" data-testid="reader-content">
                        <style dangerouslySetInnerHTML={{ __html: `
                            ${styles}
                            .reader-content-area { scroll-behavior: smooth; }
                            .reader-content-area .page-container { max-width: 800px; margin: 0 auto; }
                            .reader-content-area h1, .reader-content-area h2, .reader-content-area h3, .reader-content-area h4,
                            .reader-content-area [id^="toc-"], .reader-content-area [id^="chapter-"],
                            .reader-content-area #copyright, .reader-content-area #toc-full, .reader-content-area #cover,
                            .reader-content-area a[id^="toc-"] {
                                scroll-margin-top: 0px;
                            }
                            .reader-highlight-gold { background-color: rgba(253, 230, 138, 0.5); border-radius: 2px; cursor: pointer; }
                            .reader-highlight-green { background-color: rgba(187, 247, 208, 0.5); border-radius: 2px; cursor: pointer; }
                            .reader-highlight-blue { background-color: rgba(191, 219, 254, 0.5); border-radius: 2px; cursor: pointer; }
                            .reader-highlight-pink { background-color: rgba(251, 207, 232, 0.5); border-radius: 2px; cursor: pointer; }
                            .reader-content-area .mobile-toc-toggle { display: flex !important; }
                            .reader-content-area a.reader-anchor { display: block; height: 0; margin: 0; padding: 0; position: relative; top: 0; visibility: hidden; }
                            .reader-content-area .reader-highlight-flash { animation: reader-flash 2.2s ease-out; }
                            @keyframes reader-flash {
                                0% { background-color: rgba(255, 215, 0, 0.65); box-shadow: 0 0 0 4px rgba(255, 215, 0, 0.45); }
                                70% { background-color: rgba(255, 215, 0, 0.35); box-shadow: 0 0 0 6px rgba(255, 215, 0, 0.2); }
                                100% { background-color: transparent; box-shadow: none; }
                            }
                        `}} />
                        <div dangerouslySetInnerHTML={{ __html: html }} />
                        <style dangerouslySetInnerHTML={{ __html: `#sidebar.sidebar { display: none !important; }` }} />
                    </div>

                    {/* Desktop nav arrows */}
                    <div className="absolute bottom-[90px] right-[24px] flex flex-col gap-2 z-10" data-testid="reader-nav-arrows">
                        <button onClick={() => scrollPage(-1)} className="bg-[#D4AF37]/90 hover:bg-[#D4AF37] text-[#0B1235] rounded-full w-11 h-11 flex items-center justify-center shadow-lg transition-all hover:scale-110" data-testid="reader-prev-btn"><ChevronUp size={22} /></button>
                        <button onClick={() => scrollPage(1)} className="bg-[#D4AF37]/90 hover:bg-[#D4AF37] text-[#0B1235] rounded-full w-11 h-11 flex items-center justify-center shadow-lg transition-all hover:scale-110" data-testid="reader-next-btn"><ChevronDown size={22} /></button>
                    </div>
                </div>

                {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-10 md:hidden" onClick={() => setSidebarOpen(false)} />}
            </div>
        </div>
    );
}
